import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { ApiService } from 'app/providers/api.service';

/**
 * Header notification bell: the logged-in user's notifications from the server
 * (GET apiv1/notifications/my): overdue-stage alerts, escalations, stage forwards.
 * Every call fails soft: an error leaves the list as it was, it never breaks the page.
 */
@Injectable({
    providedIn: 'root'
})
export class NotificationsService
{
    // Pages a notification opens on the website, by the item it is about.
    private static readonly WEB_LINKS: { [refTyp: string]: string } = {
        PRPSL  : '/apps/sub-nav/1000139/work-monitioring/proposals/home',
        WRK    : '/apps/sub-nav/1000139/work-monitioring/work-maaster/works-list-new',
        LOA_AGR: '/apps/sub-nav/1000139/work-monitioring/work-maaster/works-list-new'
    };

    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private _current: Notification[] = [];

    constructor(private _api: ApiService)
    {
    }

    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    /**
     * Load the latest notifications (also used for the periodic refresh).
     */
    getAll(): Observable<Notification[]>
    {
        return this._api.get('notifications/my').pipe(
            map((res: any) => {
                const items = (res && res.status == 200 && res.data && res.data.items) || [];
                return items.map((n: any) => this._toNotification(n));
            }),
            tap((list: Notification[]) => this._publish(list)),
            catchError(() => of(this._current))
        );
    }

    /**
     * Mark one notification read / unread (the bell's toggle).
     */
    update(id: string, notification: Notification): Observable<Notification>
    {
        return this._api.post({ ntfy_id: Number(id), rd_in: notification.read ? 1 : 0 }, 'notifications/read').pipe(
            map(() => notification),
            tap(() => this._publish(this._current.map(n => n.id === id ? { ...n, read: notification.read } : n))),
            catchError(() => of(notification))
        );
    }

    /**
     * Remove a notification from the user's list.
     */
    delete(id: string): Observable<boolean>
    {
        return this._api.post({ ntfy_id: Number(id) }, 'notifications/dismiss').pipe(
            map(() => true),
            tap(() => this._publish(this._current.filter(n => n.id !== id))),
            catchError(() => of(false))
        );
    }

    markAllAsRead(): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(() => this._api.post({}, 'notifications/read-all').pipe(
                map(() => true),
                tap(() => this._publish(this._current.map(n => ({ ...n, read: true })))),
                catchError(() => of(false))
            ))
        );
    }

    /**
     * Not used: notifications are created by the server.
     */
    create(notification: Notification): Observable<Notification>
    {
        return of(notification);
    }

    private _publish(list: Notification[]): void
    {
        this._current = list;
        this._notifications.next(list);
    }

    private _toNotification(n: any): Notification
    {
        const escalation = Number(n.alrt_lvl) === 2;
        const alert = !!n.ref_typ;
        const link = n.ref_typ ? NotificationsService.WEB_LINKS[n.ref_typ] : null;
        return {
            id         : String(n.ntfy_id),
            icon       : escalation ? 'heroicons_outline:exclamation' : (alert ? 'heroicons_outline:clock' : 'heroicons_outline:bell'),
            title      : n.title,
            description: n.body,
            time       : n.ntfctn_ts,
            link       : link || undefined,
            useRouter  : !!link,
            read       : Number(n.rd_in) === 1
        };
    }
}
